import { useEffect, useRef, useState } from "react";

export const CONTACT_CENTER_BEHIND_MODALS_Z_INDEX = 1250;

const FLOATING_UI_HOST_SELECTOR = "[data-contact-center-shell], [data-chat-dock]";

const OVERLAY_SELECTOR = [
  ".MuiDialog-root",
  ".MuiModal-root",
  '[role="dialog"][aria-modal="true"]',
  "[data-app-modal]",
  "[data-modal-drag-handle]",
].join(",");

const isIgnoredOverlay = (el: HTMLElement) =>
  Boolean(
    el.classList.contains("MuiPopover-root") ||
    el.classList.contains("MuiMenu-root") ||
    el.classList.contains("MuiTooltip-popper") ||
    el.closest(".MuiPopover-root, .MuiMenu-root, .MuiTooltip-popper, .MuiAutocomplete-popper, .MuiDrawer-root"),
  );

const isVisibleOverlay = (el: HTMLElement) => {
  if (el.hasAttribute("hidden") || el.classList.contains("MuiModal-hidden")) return false;
  const style = window.getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden") return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

export const isEventInsideAppModal = (target: EventTarget | null) => {
  const el = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
  if (!el || el.closest(FLOATING_UI_HOST_SELECTOR)) return false;

  const hit = el.closest(
    ".MuiDialog-root, [data-app-modal], [data-modal-drag-handle], [role='dialog'][aria-modal='true']",
  );
  if (!(hit instanceof HTMLElement) || hit.closest(FLOATING_UI_HOST_SELECTOR)) return false;
  if (isIgnoredOverlay(hit)) return false;
  return true;
};

const resolveOverlayRoot = (node: Element): HTMLElement | null => {
  if (!(node instanceof HTMLElement)) return null;
  if (node.closest(FLOATING_UI_HOST_SELECTOR)) return null;

  if (node.hasAttribute("data-modal-drag-handle")) {
    let current: HTMLElement | null = node;
    while (current && current !== document.body) {
      if (window.getComputedStyle(current).position === "fixed") {
        if (current.closest(FLOATING_UI_HOST_SELECTOR)) return null;
        return current;
      }
      current = current.parentElement;
    }
    return null;
  }

  if (isIgnoredOverlay(node)) return null;
  if (!isVisibleOverlay(node)) return null;
  return node;
};

const readModalStack = () => {
  const roots = new Set<HTMLElement>();
  document.querySelectorAll(OVERLAY_SELECTOR).forEach((node) => {
    const root = resolveOverlayRoot(node);
    if (root) roots.add(root);
  });

  let maxZ = 0;
  roots.forEach((el) => {
    const z = Number.parseInt(window.getComputedStyle(el).zIndex, 10);
    if (Number.isFinite(z)) maxZ = Math.max(maxZ, z);
  });

  return { count: roots.size, maxZ };
};

export const useAppModalStack = () => {
  const [stack, setStack] = useState({ count: 0, maxZ: 0 });
  const frameRef = useRef<number | null>(null);
  const lastRef = useRef(stack);

  useEffect(() => {
    if (typeof MutationObserver === "undefined" || typeof document === "undefined") return undefined;

    const publish = () => {
      const next = readModalStack();
      const prev = lastRef.current;
      if (prev.count === next.count && prev.maxZ === next.maxZ) return;
      lastRef.current = next;
      setStack(next);
    };

    const schedule = () => {
      if (frameRef.current != null) return;
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        publish();
      });
    };

    publish();

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden", "aria-modal", "open"],
    });

    return () => {
      observer.disconnect();
      if (frameRef.current != null) window.cancelAnimationFrame(frameRef.current);
    };
  }, []);

  return stack;
};
